import {
  DEFAULT_NUMERIC_ANSWER_CONFIG,
  QuestionTypes,
  NumericAnswerMode as SocrativeNumericAnswerMode,
} from '@showbie-socrative/socrative-utils';

import { NumericAnswerMode } from '~analytics/types';
import { savedAcceptEquivalent } from '~teacher/components/QuestionNumeric/numericAnswerConfig';
import { NumericAnswerConfig } from '~teacher/models/quizzes';

// Record, not a switch with a default: adding a socrative-utils mode this
// map doesn't cover fails the build instead of silently reporting Exact
// (SOC-5994 review).
const MODE_MAP: Record<SocrativeNumericAnswerMode, NumericAnswerMode> = {
  [SocrativeNumericAnswerMode.ExactValue]: NumericAnswerMode.Exact,
  [SocrativeNumericAnswerMode.Expression]: NumericAnswerMode.Expression,
  [SocrativeNumericAnswerMode.Range]: NumericAnswerMode.Range,
};

/**
 * Maps a saved question's numeric answer_config to the numeric_answer_mode
 * / numeric_accept_equivalent analytics properties (SOC-5994). Both null
 * for a non-Numeric question — they only ever apply to Numeric. A Numeric
 * question with no stored config (new, or predates SOC-5990/5991) maps to
 * DEFAULT_NUMERIC_ANSWER_CONFIG's mode/accept_equivalent, since that's what
 * it actually grades as. accept_equivalent is reported as saved, so always
 * false for Expression, even while the editor keeps the teacher's choice for
 * a switch back to Exact value. A stored mode this repo doesn't recognize (data
 * corruption, or a socrative-utils release ahead of this mapper) reports
 * numeric_answer_mode: null rather than guessing Exact, so it shows up as a
 * gap in Amplitude instead of silently misclassifying.
 */
export function mapNumericAnswerConfig(
  questionType: QuestionTypes | string,
  answerConfig:
    | Pick<NumericAnswerConfig, 'mode' | 'acceptEquivalent'>
    | null
    | undefined
): {
  numeric_answer_mode: NumericAnswerMode | null;
  numeric_accept_equivalent: boolean | null;
} {
  if (questionType !== QuestionTypes.Numeric) {
    return { numeric_answer_mode: null, numeric_accept_equivalent: null };
  }
  const mode = answerConfig?.mode ?? SocrativeNumericAnswerMode.ExactValue;
  return {
    numeric_answer_mode: MODE_MAP[mode] ?? null,
    numeric_accept_equivalent: savedAcceptEquivalent(
      mode,
      answerConfig?.acceptEquivalent ??
        DEFAULT_NUMERIC_ANSWER_CONFIG.accept_equivalent
    ),
  };
}
